import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, KeyRound, Copy, CheckCircle, AlertCircle, Loader2, ArrowLeft, Lock, ShieldCheck, ShieldOff } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { toast } from 'sonner';

const MFASetupPage = () => {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState('loading'); // loading, status, setup, verify, backup, complete, disable
  const [mfaData, setMfaData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [disableCode, setDisableCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedBackup, setCopiedBackup] = useState(false);

  const checkMFAStatus = useCallback(async () => {
    try {
      const userData = await refreshUser();
      if (userData?.mfa_enabled) {
        setStep('status');
      } else {
        setStep('setup');
      }
    } catch (err) {
      setStep('setup');
    }
  }, [refreshUser]);

  useEffect(() => {
    checkMFAStatus();
  }, [checkMFAStatus]);

  const initMFASetup = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await api.post('/auth/mfa/setup', {});
      setMfaData(response.data);
      setStep('verify');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao iniciar configuracao MFA');
      if (err.response?.data?.detail === 'MFA ja esta ativado') {
        setStep('status');
      }
    } finally {
      setLoading(false);
    }
  };

  const verifyMFA = async () => {
    if (verifyCode.length !== 6) {
      setError('Introduza um codigo de 6 digitos');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await api.post('/auth/mfa/verify', { code: verifyCode });
      setStep('backup');
      toast.success('MFA ativado com sucesso!');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codigo invalido. Verifique e tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (disableCode.length < 6) {
      setError('Introduza um codigo valido');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      await api.post('/auth/mfa/disable', { code: disableCode });
      await refreshUser();
      setStep('setup');
      setDisableCode('');
      toast.success('MFA desativado com sucesso');
    } catch (err) {
      setError(err.response?.data?.detail || 'Codigo invalido');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'secret') {
        setCopiedSecret(true);
        setTimeout(() => setCopiedSecret(false), 2000);
      } else {
        setCopiedBackup(true);
        setTimeout(() => setCopiedBackup(false), 2000);
      }
      toast.success('Copiado para a area de transferencia');
    } catch (err) {
      toast.error('Erro ao copiar');
    }
  };

  const finishSetup = async () => {
    await refreshUser();
    navigate('/admin/definicoes');
  };

  // Loading state
  if (step === 'loading') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-lg mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/admin/definicoes')}
            className="mb-4"
            data-testid="mfa-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar as Definicoes
          </Button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>
                Autenticacao Multi-Fator
              </h1>
              <p className="text-slate-500">Proteja a sua conta com verificacao em 2 passos</p>
            </div>
          </div>
        </div>

        {error && (
          <Alert className="mb-6 bg-red-50 border-red-200">
            <AlertCircle className="w-4 h-4 text-red-600" />
            <AlertDescription className="text-red-700">{error}</AlertDescription>
          </Alert>
        )}

        {/* MFA Status - Already Enabled */}
        {step === 'status' && (
          <Card>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle>MFA Ativado</CardTitle>
              <CardDescription>
                A sua conta esta protegida com autenticacao de dois fatores
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-800">
                  Sera solicitado um codigo de verificacao sempre que fizer login.
                </p>
              </div>
              
              <Separator />
              
              <div>
                <h4 className="font-medium text-slate-900 mb-2">Desativar MFA</h4>
                <p className="text-sm text-slate-500 mb-3">
                  Introduza o codigo da sua app de autenticacao para desativar.
                </p>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    className="text-center font-mono tracking-widest"
                    data-testid="mfa-disable-code-input"
                  />
                  <Button 
                    variant="destructive"
                    onClick={disableMFA}
                    disabled={loading || disableCode.length < 6}
                    data-testid="mfa-disable-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Setup - Show option to enable */}
        {step === 'setup' && (
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-8 h-8 text-slate-600" />
              </div>
              <CardTitle>Ativar MFA</CardTitle>
              <CardDescription>
                Adicione uma camada extra de seguranca a sua conta
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <h4 className="font-medium text-amber-800 mb-2">O que precisa</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>- Uma app de autenticacao (Google Authenticator, Authy, etc.)</li>
                  <li>- Alguns minutos para configurar</li>
                </ul>
              </div>
              
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="font-medium text-slate-900 mb-2">Como funciona</h4>
                <ol className="text-sm text-slate-600 space-y-2">
                  <li>1. Digitalize o codigo QR com a sua app</li>
                  <li>2. Introduza o codigo de 6 digitos para verificar</li>
                  <li>3. Guarde os codigos de backup em local seguro</li>
                </ol>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                onClick={initMFASetup}
                disabled={loading}
                data-testid="mfa-start-setup-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A gerar...
                  </>
                ) : (
                  <>
                    <KeyRound className="w-4 h-4 mr-2" />
                    Iniciar Configuracao
                  </>
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 2: Verify - Show QR code and verify */}
        {step === 'verify' && mfaData && (
          <Card>
            <CardHeader>
              <CardTitle>Passo 1: Digitalize o Codigo QR</CardTitle>
              <CardDescription>
                Abra a sua app de autenticacao e digitalize este codigo
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* QR Code */}
              <div className="flex justify-center">
                <div className="p-4 bg-white rounded-xl border-2 border-slate-200 shadow-sm">
                  <img 
                    src={mfaData.qr_code} 
                    alt="QR Code para MFA" 
                    className="w-48 h-48"
                    data-testid="mfa-qr-code"
                  />
                </div>
              </div>

              {/* Manual entry option */}
              <div className="bg-slate-50 rounded-lg p-4">
                <p className="text-sm text-slate-600 mb-2">Nao consegue digitalizar? Introduza manualmente:</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-white px-3 py-2 rounded border font-mono break-all">
                    {mfaData.secret}
                  </code>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(mfaData.secret, 'secret')}
                    data-testid="mfa-copy-secret-btn"
                  >
                    {copiedSecret ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>

              <Separator />

              {/* Verify code */}
              <div>
                <Label htmlFor="verify-code">Passo 2: Introduza o codigo de verificacao</Label>
                <p className="text-sm text-slate-500 mb-3">
                  Introduza o codigo de 6 digitos da sua app de autenticacao
                </p>
                <Input
                  id="verify-code"
                  type="text"
                  value={verifyCode}
                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="text-center text-2xl tracking-widest font-mono"
                  autoComplete="one-time-code"
                  data-testid="mfa-verify-code-input"
                />
              </div>
            </CardContent>
            <CardFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => setStep('setup')}
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button 
                onClick={verifyMFA}
                disabled={loading || verifyCode.length !== 6}
                className="flex-1"
                data-testid="mfa-verify-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    A verificar...
                  </>
                ) : (
                  'Verificar e Ativar'
                )}
              </Button>
            </CardFooter>
          </Card>
        )}

        {/* Step 3: Backup codes */}
        {step === 'backup' && mfaData && (
          <Card>
            <CardHeader className="text-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
              <CardTitle>MFA Ativado com Sucesso!</CardTitle>
              <CardDescription>
                Guarde os codigos de backup num local seguro
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Alert className="bg-amber-50 border-amber-200">
                <AlertCircle className="w-4 h-4 text-amber-600" />
                <AlertDescription className="text-amber-800">
                  Estes codigos so serao mostrados uma vez. Guarde-os num local seguro.
                  Cada codigo so pode ser usado uma vez.
                </AlertDescription>
              </Alert>

              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-slate-900">Codigos de Backup</h4>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => copyToClipboard(mfaData.backup_codes.join('\n'), 'backup')}
                    data-testid="mfa-copy-backup-btn"
                  >
                    {copiedBackup ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                    <span className="ml-2">Copiar</span>
                  </Button>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {mfaData.backup_codes.map((code, idx) => (
                    <code 
                      key={idx} 
                      className="text-sm bg-white px-3 py-2 rounded border font-mono text-center"
                      data-testid={`mfa-backup-code-${idx}`}
                    >
                      {code}
                    </code>
                  ))}
                </div>
              </div>
            </CardContent>
            <CardFooter>
              <Button 
                className="w-full" 
                onClick={finishSetup}
                data-testid="mfa-finish-btn"
              >
                Concluir Configuracao
              </Button>
            </CardFooter>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MFASetupPage;
